import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../i18n';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  Plus, Search, Building2, Globe, Phone, Mail, MapPin, 
  Edit, Trash2, Upload, Image, Users, FileText, Calendar,
  Briefcase, X, ChevronLeft
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CompaniesPage = () => {
  const { user } = useAuth();
  const t = useTranslation(user?.language || 'ja');
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companyDetails, setCompanyDetails] = useState(null);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [mobileShowDetail, setMobileShowDetail] = useState(false);
  const fileInputRef = useRef(null);

  const [form, setForm] = useState({
    name: '',
    address: '',
    phone: '',
    email: '',
    website: '',
    industry: '',
    employee_count: '',
    founded_year: '',
    description: '',
    tags: '',
  });

  useEffect(() => {
    fetchCompanies();
  }, [search]);

  const fetchCompanies = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      
      const { data } = await axios.get(`${API_URL}/api/companies?${params}`, {
        withCredentials: true,
      });
      setCompanies(data.companies);
    } catch (e) {
      console.error('Failed to fetch companies:', e);
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanyDetails = async (companyId) => {
    try {
      const [companyRes, contactsRes, filesRes] = await Promise.all([
        axios.get(`${API_URL}/api/companies/${companyId}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/companies/${companyId}/contacts`, { withCredentials: true }),
        axios.get(`${API_URL}/api/companies/${companyId}/files`, { withCredentials: true }),
      ]);
      setCompanyDetails({
        ...companyRes.data,
        contacts: contactsRes.data.contacts,
        files: filesRes.data.files,
      });
    } catch (e) {
      console.error('Failed to fetch company details:', e);
    }
  };

  const handleSelectCompany = (company) => {
    setSelectedCompany(company);
    fetchCompanyDetails(company.id);
    setMobileShowDetail(true);
  };

  const handleCreate = async () => {
    if (!form.name.trim()) {
      toast.error(user?.language === 'ja' ? '会社名を入力してください' : 'Please enter company name');
      return;
    }
    
    try {
      const payload = {
        ...form,
        tags: form.tags.split(',').map(t => t.trim()).filter(Boolean),
      };
      const { data } = await axios.post(`${API_URL}/api/companies`, payload, {
        withCredentials: true,
      });
      toast.success(t.common.success);
      setIsCreateOpen(false);
      resetForm();
      fetchCompanies();
      handleSelectCompany(data);
    } catch (e) {
      toast.error(t.common.error);
    }
  };

  const handleUpdate = async () => {
    if (!selectedCompany) return;
    
    try {
      const payload = {
        ...form,
        tags: form.tags.split(',').map(t => t.trim()).filter(Boolean),
      };
      await axios.put(`${API_URL}/api/companies/${selectedCompany.id}`, payload, {
        withCredentials: true,
      });
      toast.success(t.common.success);
      setIsEditOpen(false);
      fetchCompanies();
      fetchCompanyDetails(selectedCompany.id);
    } catch (e) {
      toast.error(t.common.error);
    }
  };

  const handleDelete = async (companyId) => {
    if (!window.confirm(t.common.confirm)) return;
    
    try {
      await axios.delete(`${API_URL}/api/companies/${companyId}`, {
        withCredentials: true,
      });
      toast.success(t.common.success);
      setSelectedCompany(null);
      setCompanyDetails(null);
      fetchCompanies();
    } catch (e) {
      toast.error(t.common.error);
    }
  };

  const handleLogoUpload = async (e) => {
    if (!selectedCompany || !e.target.files?.[0]) return;
    
    const file = e.target.files[0];
    if (!file.type.startsWith('image/')) {
      toast.error(user?.language === 'ja' ? '画像ファイルを選択してください' : 'Please select an image file');
      return;
    }
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      await axios.post(`${API_URL}/api/companies/${selectedCompany.id}/logo`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(user?.language === 'ja' ? 'ロゴをアップロードしました' : 'Logo uploaded');
      fetchCompanyDetails(selectedCompany.id);
      fetchCompanies();
    } catch (e) {
      toast.error(t.common.error);
    } finally {
      setUploading(false);
    }
  };

  const resetForm = () => {
    setForm({
      name: '',
      address: '',
      phone: '',
      email: '',
      website: '',
      industry: '',
      employee_count: '',
      founded_year: '',
      description: '',
      tags: '',
    });
  };

  const openEditDialog = (company) => {
    setForm({
      name: company.name || '',
      address: company.address || '',
      phone: company.phone || '',
      email: company.email || '',
      website: company.website || '',
      industry: company.industry || '',
      employee_count: company.employee_count || '',
      founded_year: company.founded_year || '',
      description: company.description || '',
      tags: (company.tags || []).join(', '),
    });
    setIsEditOpen(true);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return new Intl.DateTimeFormat(user?.language === 'en' ? 'en-US' : 'ja-JP', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(date);
  };

  const CompanyForm = ({ onSubmit, submitText }) => (
    <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2 sm:col-span-2">
          <Label>{user?.language === 'ja' ? '会社名' : 'Company Name'} *</Label>
          <Input
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="input-cyber"
            data-testid="company-name-input"
          />
        </div>
        <div className="space-y-2">
          <Label>{user?.language === 'ja' ? '業種' : 'Industry'}</Label>
          <Input
            value={form.industry}
            onChange={(e) => setForm({ ...form, industry: e.target.value })}
            className="input-cyber"
            placeholder={user?.language === 'ja' ? 'IT、製造業など' : 'IT, Manufacturing, etc.'}
          />
        </div>
        <div className="space-y-2">
          <Label>{user?.language === 'ja' ? '従業員数' : 'Employees'}</Label>
          <Input
            value={form.employee_count}
            onChange={(e) => setForm({ ...form, employee_count: e.target.value })}
            className="input-cyber"
            placeholder={user?.language === 'ja' ? '100名' : '100'}
          />
        </div>
      </div>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>{user?.language === 'ja' ? '電話番号' : 'Phone'}</Label>
          <Input
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            className="input-cyber"
          />
        </div>
        <div className="space-y-2">
          <Label>{user?.language === 'ja' ? 'メール' : 'Email'}</Label>
          <Input
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="input-cyber"
          />
        </div>
      </div>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>{user?.language === 'ja' ? 'ウェブサイト' : 'Website'}</Label>
          <Input
            value={form.website}
            onChange={(e) => setForm({ ...form, website: e.target.value })}
            className="input-cyber"
            placeholder="https://example.com"
          />
        </div>
        <div className="space-y-2">
          <Label>{user?.language === 'ja' ? '設立年' : 'Founded'}</Label>
          <Input
            value={form.founded_year}
            onChange={(e) => setForm({ ...form, founded_year: e.target.value })}
            className="input-cyber"
            placeholder="2020"
          />
        </div>
      </div>
      
      <div className="space-y-2">
        <Label>{user?.language === 'ja' ? '住所' : 'Address'}</Label>
        <Input
          value={form.address}
          onChange={(e) => setForm({ ...form, address: e.target.value })}
          className="input-cyber"
        />
      </div>
      
      <div className="space-y-2">
        <Label>{user?.language === 'ja' ? 'タグ' : 'Tags'}</Label>
        <Input
          value={form.tags}
          onChange={(e) => setForm({ ...form, tags: e.target.value })}
          className="input-cyber"
          placeholder={user?.language === 'ja' ? 'タグ1, タグ2' : 'tag1, tag2'}
        />
      </div>
      
      <div className="space-y-2">
        <Label>{user?.language === 'ja' ? '説明' : 'Description'}</Label>
        <Textarea
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          className="input-cyber min-h-[80px]"
        />
      </div>
      
      <Button onClick={onSubmit} className="w-full btn-primary" data-testid="company-submit-btn">
        {submitText}
      </Button>
    </div>
  );

  const CompanyListPanel = () => (
    <Card className="card-cyber flex-1 flex flex-col">
      <CardHeader className="pb-3 px-3 sm:px-6">
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="text-base sm:text-lg flex items-center gap-2">
            <Building2 className="w-5 h-5 text-primary" />
            {user?.language === 'ja' ? '会社管理' : 'Companies'}
          </CardTitle>
          <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
            <DialogTrigger asChild>
              <Button size="sm" className="btn-primary" data-testid="add-company-btn">
                <Plus className="w-4 h-4 mr-1" />
                {user?.language === 'ja' ? '追加' : 'Add'}
              </Button>
            </DialogTrigger>
            <DialogContent className="card-cyber border-glow max-w-[95vw] sm:max-w-lg">
              <DialogHeader>
                <DialogTitle>{user?.language === 'ja' ? '会社を追加' : 'Add Company'}</DialogTitle>
              </DialogHeader>
              <CompanyForm onSubmit={handleCreate} submitText={t.common.save} />
            </DialogContent>
          </Dialog>
        </div>
        
        <div className="relative mt-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t.search.placeholder}
            className="pl-9 input-cyber"
            data-testid="company-search-input"
          />
        </div>
      </CardHeader>
      
      <CardContent className="flex-1 p-0">
        <ScrollArea className="h-full">
          {loading ? (
            <div className="p-4 text-center text-muted-foreground">{t.common.loading}</div>
          ) : companies.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              <Building2 className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>{user?.language === 'ja' ? '会社がありません' : 'No companies found'}</p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {companies.map((company) => (
                <div
                  key={company.id}
                  onClick={() => handleSelectCompany(company)}
                  className={`p-3 sm:p-4 cursor-pointer hover:bg-secondary transition-colors ${
                    selectedCompany?.id === company.id ? 'bg-secondary border-l-2 border-l-primary' : ''
                  }`}
                  data-testid={`company-item-${company.id}`}
                >
                  <div className="flex items-center gap-3">
                    {company.logo_path ? (
                      <img
                        src={`${API_URL}/api/companies/${company.id}/logo`}
                        alt={company.name}
                        className="w-10 h-10 rounded-sm object-cover bg-secondary flex-shrink-0"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-sm bg-primary/20 flex items-center justify-center flex-shrink-0">
                        <Building2 className="w-5 h-5 text-primary" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{company.name}</p>
                      {company.industry && (
                        <p className="text-xs text-muted-foreground truncate">{company.industry}</p>
                      )}
                    </div>
                  </div>
                  {company.tags?.length > 0 && (
                    <div className="flex gap-1 mt-2 flex-wrap">
                      {company.tags.slice(0, 3).map((tag, idx) => (
                        <Badge key={idx} variant="outline" className="text-xs bg-secondary">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </CardContent>
    </Card>
  );

  const CompanyDetailPanel = () => (
    <>
      {selectedCompany && companyDetails ? (
        <Card className="card-cyber h-full flex flex-col" data-testid="company-detail">
          <CardHeader className="pb-3 px-3 sm:px-6">
            {/* Mobile back button */}
            <button 
              onClick={() => setMobileShowDetail(false)} 
              className="md:hidden text-sm text-primary mb-2 flex items-center gap-1"
              data-testid="company-back-btn"
            >
              <ChevronLeft className="w-4 h-4" />
              {user?.language === 'ja' ? '一覧に戻る' : 'Back to list'}
            </button>
            <div className="flex items-start gap-3 sm:gap-4">
              {/* Logo */}
              <div className="relative group flex-shrink-0">
                {companyDetails.logo_path ? (
                  <img
                    src={`${API_URL}/api/companies/${selectedCompany.id}/logo?t=${Date.now()}`}
                    alt={companyDetails.name}
                    className="w-14 h-14 sm:w-20 sm:h-20 rounded-sm object-cover bg-secondary"
                  />
                ) : (
                  <div className="w-14 h-14 sm:w-20 sm:h-20 rounded-sm bg-primary/20 flex items-center justify-center">
                    <Building2 className="w-7 h-7 sm:w-10 sm:h-10 text-primary" />
                  </div>
                )}
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center rounded-sm"
                  disabled={uploading}
                >
                  <Upload className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleLogoUpload}
                />
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <CardTitle className="text-lg sm:text-xl truncate">{companyDetails.name}</CardTitle>
                    {companyDetails.industry && (
                      <Badge variant="outline" className="mt-1 text-xs">
                        <Briefcase className="w-3 h-3 mr-1" />
                        {companyDetails.industry}
                      </Badge>
                    )}
                  </div>
                  <div className="flex gap-1 sm:gap-2 flex-shrink-0">
                    <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
                      <DialogTrigger asChild>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openEditDialog(companyDetails)}
                          data-testid="edit-company-btn"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="card-cyber border-glow max-w-[95vw] sm:max-w-lg">
                        <DialogHeader>
                          <DialogTitle>{t.common.edit}</DialogTitle>
                        </DialogHeader>
                        <CompanyForm onSubmit={handleUpdate} submitText={t.common.save} />
                      </DialogContent>
                    </Dialog>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-destructive hover:bg-destructive/10"
                      onClick={() => handleDelete(selectedCompany.id)}
                      data-testid="delete-company-btn"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Company Info */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3 mt-4 text-sm">
              {companyDetails.email && (
                <div className="flex items-center gap-2 text-muted-foreground truncate">
                  <Mail className="w-4 h-4 text-primary flex-shrink-0" />
                  <span className="truncate">{companyDetails.email}</span>
                </div>
              )}
              {companyDetails.phone && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Phone className="w-4 h-4 text-primary flex-shrink-0" />
                  {companyDetails.phone}
                </div>
              )}
              {companyDetails.website && (
                <div className="flex items-center gap-2 text-muted-foreground truncate">
                  <Globe className="w-4 h-4 text-primary flex-shrink-0" />
                  <a href={companyDetails.website} target="_blank" rel="noopener noreferrer" className="hover:text-primary truncate">
                    {companyDetails.website}
                  </a>
                </div>
              )}
              {companyDetails.address && (
                <div className="flex items-center gap-2 text-muted-foreground sm:col-span-2 truncate">
                  <MapPin className="w-4 h-4 text-primary flex-shrink-0" />
                  <span className="truncate">{companyDetails.address}</span>
                </div>
              )}
              {companyDetails.employee_count && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Users className="w-4 h-4 text-primary flex-shrink-0" />
                  {companyDetails.employee_count} {user?.language === 'ja' ? '名' : 'employees'}
                </div>
              )}
              {companyDetails.founded_year && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Calendar className="w-4 h-4 text-primary flex-shrink-0" />
                  {user?.language === 'ja' ? `${companyDetails.founded_year}年設立` : `Founded ${companyDetails.founded_year}`}
                </div>
              )}
            </div>
            
            {companyDetails.description && (
              <p className="mt-4 text-sm text-muted-foreground">{companyDetails.description}</p>
            )}
          </CardHeader>
          
          <CardContent className="flex-1 p-0 overflow-hidden">
            <Tabs defaultValue="contacts" className="h-full flex flex-col">
              <TabsList className="px-2 sm:px-4 bg-transparent border-b border-border rounded-none h-auto justify-start">
                <TabsTrigger value="contacts" className="data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none text-xs sm:text-sm">
                  <Users className="w-4 h-4 mr-1" />
                  {user?.language === 'ja' ? '連絡先' : 'Contacts'} ({companyDetails.contacts?.length || 0})
                </TabsTrigger>
                <TabsTrigger value="files" className="data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none text-xs sm:text-sm">
                  <FileText className="w-4 h-4 mr-1" />
                  {user?.language === 'ja' ? '書類' : 'Files'} ({companyDetails.files?.length || 0})
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="contacts" className="flex-1 p-3 sm:p-4 m-0">
                <ScrollArea className="h-[250px]">
                  {companyDetails.contacts?.length > 0 ? (
                    <div className="space-y-2">
                      {companyDetails.contacts.map((contact) => (
                        <div key={contact.id} className="flex items-center gap-3 p-3 rounded-sm bg-secondary/50 hover:bg-secondary">
                          <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                            <span className="text-sm font-medium text-primary">
                              {contact.name?.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate text-sm">{contact.name}</p>
                            {contact.email && (
                              <p className="text-xs text-muted-foreground truncate">{contact.email}</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">{user?.language === 'ja' ? '連絡先がありません' : 'No contacts'}</p>
                    </div>
                  )}
                </ScrollArea>
              </TabsContent>
              
              <TabsContent value="files" className="flex-1 p-3 sm:p-4 m-0">
                <ScrollArea className="h-[250px]">
                  {companyDetails.files?.length > 0 ? (
                    <div className="space-y-2">
                      {companyDetails.files.map((file) => (
                        <div key={file.id} className="flex items-center gap-3 p-3 rounded-sm bg-secondary/50 hover:bg-secondary">
                          <FileText className="w-5 h-5 text-primary flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate text-sm">{file.title}</p>
                            <p className="text-xs text-muted-foreground font-mono">{formatDate(file.created_at)}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">{user?.language === 'ja' ? '書類がありません' : 'No files'}</p>
                    </div>
                  )}
                </ScrollArea>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      ) : (
        <Card className="card-cyber h-full flex items-center justify-center">
          <div className="text-center text-muted-foreground">
            <Building2 className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-4 opacity-30" />
            <p className="text-sm">{user?.language === 'ja' ? '会社を選択してください' : 'Select a company to view details'}</p>
          </div>
        </Card>
      )}
    </>
  );

  return (
    <div className="h-[calc(100vh-100px)]" data-testid="companies-page">
      {/* Desktop: side-by-side layout */}
      <div className="hidden md:flex h-full gap-4">
        <div className="w-1/3 flex flex-col">
          <CompanyListPanel />
        </div>
        <div className="flex-1">
          <CompanyDetailPanel />
        </div>
      </div>

      {/* Mobile: toggle between list and detail */}
      <div className="md:hidden h-full flex flex-col">
        {mobileShowDetail && selectedCompany ? (
          <CompanyDetailPanel />
        ) : (
          <CompanyListPanel />
        )}
      </div>
    </div>
  );
};

export default CompaniesPage;
